import React, { useState, useEffect } from 'react'; 
import { doc, runTransaction, collection, Timestamp } from 'firebase/firestore'; 
import { db, appId } from './firebase/config';
import { useBookingData } from './hooks/useBookingData';
import TimeStep from './components/booking/TimeStep';
import GuestsStep from './components/booking/GuestsStep';
import DetailsStep from './components/booking/DetailsStep';
import ImportantNote from './components/ui/ImportantNote';

// --- MODAL INTERNO ROBUSTO ---
const SimpleModal = ({ show, title, message, isError, onClose }) => {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 999999,
            backdropFilter: 'blur(4px)'
        }}>
            <div style={{
                backgroundColor: 'white', padding: '30px', borderRadius: '12px',
                width: '90%', maxWidth: '400px', textAlign: 'center',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
                <div style={{ fontSize: '40px', marginBottom: '16px', color: isError ? '#ef4444' : '#10b981' }}>
                    <i className={`fas ${isError ? 'fa-times-circle' : 'fa-check-circle'}`}></i>
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px', color: '#111' }}>{title}</h3>
                <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: '24px', whiteSpace: 'pre-line' }}>{message}</p>
                <button onClick={onClose} style={{
                    backgroundColor: isError ? '#ef4444' : '#10b981', color: 'white',
                    padding: '10px 24px', borderRadius: '6px', border: 'none', fontWeight: 'bold', cursor: 'pointer', width: '100%'
                }}>
                    Fechar
                </button>
            </div>
        </div>
    );
};

const BookingWidget = () => {
    const [activeSection, setActiveSection] = useState('date');
    const [formState, setFormState] = useState({ date: '', time: '', guests: '', isGroup: false });
    const [currentDisplayDate, setCurrentDisplayDate] = useState(new Date());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modal, setModal] = useState({ show: false, title: '', message: '', isError: false });
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [origemDetetada, setOrigemDetetada] = useState('Website'); 

    const { bookingSettings, loading, getDayStatus, getBestDiscountForTime, calculateDiscountBreakdown, events, dailyAvailability } = useBookingData();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        if (ref) {
            setOrigemDetetada(ref);
        }
    }, []);

    const closeModal = () => {
        setModal({ ...modal, show: false });
        if (!modal.isError) {
            setActiveSection('date');
            setFormState({ date: '', time: '', guests: '', isGroup: false });
            setSelectedEvent(null);
        }
    };

    const goToStep = (step) => { 
        if (step === 'date') setActiveSection('date'); 
        else if (step === 'time' && formState.date) setActiveSection('time'); 
        else if (step === 'guests' && formState.date && formState.time) setActiveSection('guests'); 
    };
    const handleDateSelect = (dateStr) => { 
        const dayStatus = getDayStatus({ ano: parseInt(dateStr.split('-')[0]), mes: parseInt(dateStr.split('-')[1]), dia: parseInt(dateStr.split('-')[2]) }); 
        if (dayStatus.isEvent) { 
            const eventDetails = events.find(e => e.nomeEvento === dayStatus.eventName); 
            setSelectedEvent(eventDetails); 
            setFormState(prev => ({ ...prev, date: dateStr, time: '', isGroup: false })); 
            setActiveSection('eventDetails'); 
        } else { 
            setSelectedEvent(null)
            setFormState(prev => ({ ...prev, date: dateStr, time: '', guests: '' })); 
            setActiveSection('time'); 
        } 
    };

    const handleTimeSelect = (time) => { setFormState(prev => ({ ...prev, time, guests: '' })); setActiveSection('guests'); };
    const handleGuestsSelect = (guests, isGroup = false) => { setFormState(prev => ({ ...prev, guests: String(guests), isGroup })); setActiveSection('details'); };

    const handleDetailsSubmit = async (detailsFromForm) => {
        setIsSubmitting(true);
        const { date, time, guests, isGroup } = formState;

        if (!bookingSettings || !bookingSettings.horarios) {
            setModal({ show: true, title: "Erro", message: "Sistema indisponível.", isError: true });
            setIsSubmitting(false); return;
        }

        try {
            const isEventBooking = !!selectedEvent;
            const effectiveTime = time;
            const numPessoas = parseInt(guests, 10);
            let servicoKey;
            
            if (isEventBooking) { 
                servicoKey = parseInt(effectiveTime.split(':')[0]) < 17 ? 'almoco' : 'jantar'; 
            } else { 
                const almocoFim = bookingSettings.horarios.almoco.horarioCompleto.fim; 
                servicoKey = (effectiveTime <= almocoFim) ? 'almoco' : 'jantar'; 
            }

            const availabilityDocRef = doc(db, `/artifacts/${appId}/public/data/disponibilidadeDiaria`, date);
            const counterRef = doc(db, `artifacts/${appId}/public/data/disponibilidadeDiaria/contador_global_reservas`);
            const settingsRef = doc(db, `artifacts/${appId}/public/data/configuracoes`, 'sistema'); 

            const result = await runTransaction(db, async (transaction) => {
                const availabilityDoc = await transaction.get(availabilityDocRef);
                const counterDoc = await transaction.get(counterRef);
                const settingsDoc = await transaction.get(settingsRef);
                const dayData = availabilityDoc.exists() ? availabilityDoc.data() : {};
                const sysSettings = settingsDoc.exists() ? settingsDoc.data() : bookingSettings;

                let turnNum = null;
                if (!isEventBooking && sysSettings.horarios[servicoKey]?.turnosAtivos) { 
                    const t1Start = sysSettings.horarios[servicoKey].turno1.inicio;
                    const t2Start = sysSettings.horarios[servicoKey].turno2.inicio;
                    const t2End = sysSettings.horarios[servicoKey].turno2.fim;
                    if (effectiveTime >= t1Start && effectiveTime < t2Start) turnNum = 1; 
                    else if (effectiveTime >= t2Start && effectiveTime <= t2End) turnNum = 2; 
                }

                const lugaresKey = `lugaresReservados_${servicoKey}${turnNum ? `_t${turnNum}` : ''}`; 
                const currentOccupancy = dayData[lugaresKey] || 0;
                const totalLimit = sysSettings.descontos.limiteReservasOnline[servicoKey].total;
                
                if ((currentOccupancy + numPessoas) > totalLimit) throw new Error(`Sem disponibilidade.`);

                let discountMessage = "Reserva Normal";
                let appliedDiscount = 0;

                if (!isEventBooking && !isGroup && sysSettings.descontos.ativo) {
                    let inicioFatia = 0;
                    const escaloes = (sysSettings.descontos.escaloes || []).slice().sort((a, b) => b.percentagem - a.percentagem);
                    const escaloesComIntervalo = escaloes.map(e => {
                        const fatia = { ...e, inicio: inicioFatia, fim: inicioFatia + e.quantidade };
                        inicioFatia += e.quantidade;
                        return fatia;
                    });

                    let percentagesTouched = [];
                    let pessoasRestantes = numPessoas;
                    let posicaoAtual = currentOccupancy;

                    for (const escalao of escaloesComIntervalo) {
                        if (pessoasRestantes <= 0) break;
                        const overlapStart = Math.max(posicaoAtual, escalao.inicio);
                        const overlapEnd = Math.min(posicaoAtual + pessoasRestantes, escalao.fim);
                        const ocupadoAqui = Math.max(0, overlapEnd - overlapStart);

                        if (ocupadoAqui > 0) {
                            percentagesTouched.push(escalao.percentagem);
                            pessoasRestantes -= ocupadoAqui;
                            posicaoAtual += ocupadoAqui;
                        }
                    }
                    if (pessoasRestantes > 0) percentagesTouched.push(0);
                    appliedDiscount = Math.min(...percentagesTouched);
                    discountMessage = appliedDiscount > 0 ? `Promoção -${appliedDiscount}%` : "Reserva Normal";
                }

                let nextId = 1000; 
                if (counterDoc.exists()) { nextId = (counterDoc.data().current || 1000) + 1; } 
                transaction.set(counterRef, { current: nextId }, { merge: true });
                const updateData = {};
                updateData[lugaresKey] = currentOccupancy + numPessoas;
                transaction.set(availabilityDocRef, updateData, { merge: true });

                const newReservation = { 
                    idReserva: nextId, clienteNome: detailsFromForm.name, clienteEmail: detailsFromForm.email, clienteTelefone: detailsFromForm.phone, observacoes: detailsFromForm.notes, motivoGrupo: detailsFromForm.groupReason, numPessoas: numPessoas, dataReserva: date, horaReserva: effectiveTime, sessaoReserva: servicoKey, estadoReserva: isGroup ? 'pendente' : 'confirmada', dataCriacao: Timestamp.now(), isFirstVisit: true, detalhesDesconto: discountMessage, descontoAplicado: appliedDiscount, turno: turnNum, eventId: isEventBooking ? selectedEvent.id : null, nomeEvento: isEventBooking ? selectedEvent.nomeEvento : null, 
                    origem: origemDetetada 
                };
                const newReservationRef = doc(collection(db, `/artifacts/${appId}/public/data/reservas`)); 
                transaction.set(newReservationRef, newReservation);
                return { successMessage: `Reserva #${nextId} confirmada!\n${discountMessage}` };
            });

            if (result && result.successMessage) { 
                setModal({ show: true, title: "Sucesso", message: result.successMessage, isError: false }); 
            }
        } catch (error) { 
            setModal({ show: true, title: "Atenção", message: error.message || "Tente novamente.", isError: true }); 
        } finally { setIsSubmitting(false); }
    };
    // LÓGICA DE DETEÇÃO DE FECHO EXCECONAL (OBRAS / FÉRIAS)
    const verificarDiaBloqueadoExcecional = (dateToCheck) => {
        if (!bookingSettings || !bookingSettings.periodosFecho || bookingSettings.periodosFecho.length === 0) {
            return false;
        }
        
        // Criar data alvo sem horas para evitar desvios de fusos horários
        const targetDate = new Date(dateToCheck);
        targetDate.setHours(0, 0, 0, 0);
        const targetTimestamp = targetDate.getTime();

        return bookingSettings.periodosFecho.some(periodo => {
            if (!periodo.inicio || !periodo.fim) return false;

            const dateStart = new Date(periodo.inicio);
            dateStart.setHours(0, 0, 0, 0);

            const dateEnd = new Date(periodo.fim);
            dateEnd.setHours(0, 0, 0, 0);

            return targetTimestamp >= dateStart.getTime() && targetTimestamp <= dateEnd.getTime();
        });
    };

    const renderCalendar = () => {
        const month = currentDisplayDate.getMonth();
        const year = currentDisplayDate.getFullYear();
        
        const firstDayOfMonth = new Date(year, month, 1).getDay(); 
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const today = new Date(); 
        today.setHours(0, 0, 0, 0);
        const dayCells = [];
        
        for (let i = 0; i < firstDayOfMonth; i++) {
            dayCells.push(<div key={`empty-${i}`} style={{ borderBottom: '1px solid transparent' }}></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayStatus = getDayStatus({ ano: year, mes: month + 1, dia: day });
            const isPast = date < today;
            
            // NOVO: Executa a verificação de Obras no dia atual da renderização
            const estaFechadoPorObras = verificarDiaBloqueadoExcecional(date);

            // O dia só é clicável se NÃO for passado, se o status do hook permitir E se NÃO estiver trancado para obras
            const isClickable = dayStatus.status !== 'full' && !estaFechadoPorObras && (!isPast || (dayStatus.isEvent && date >= today));
            
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = formState.date === dateStr;

            dayCells.push(
                <button 
                    key={day} 
                    type="button" 
                    disabled={!isClickable} 
                    onClick={() => handleDateSelect(dateStr)} 
                    style={{
                        position: 'relative', width: '100%', height: '48px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '8px',
                        border: isSelected ? '1px solid #d97706' : '1px solid #e5e7eb',
                        backgroundColor: isSelected ? '#d97706' : (isClickable ? 'white' : '#f9fafb'),
                        color: isSelected ? 'white' : (isClickable ? '#374151' : '#d1d5db'),
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                        overflow: 'visible', boxShadow: isSelected ? '0 4px 6px rgba(217, 119, 6, 0.2)' : 'none',
                    }}
                >
                    <span style={{ fontWeight: 'bold', zIndex: 10 }}>{day}</span>
                    {/* Se estiver fechado por obras, mostra um pequeno aviso ou legenda opcional visual */}
                    {estaFechadoPorObras && (
                        <div style={{ position: 'absolute', bottom: '2px', fontSize: '8px', color: '#ef4444', fontWeight: 'bold' }}>FECHO</div>
                    )}
                    {dayStatus.status === 'promo' && !dayStatus.isEvent && isClickable && !isSelected && !estaFechadoPorObras && (
                        <div className="animate-blink-gold" style={{position: 'absolute', top: '-6px', right: '-6px', backgroundColor: '#f59e0b', color: 'white', fontSize: '9px', fontWeight: 'bold', padding: '2px 4px', borderRadius: '4px', zIndex: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.2)'}}>PROMO</div>
                    )}
                    {dayStatus.isEvent && isClickable && !isSelected && !estaFechadoPorObras && (<div style={{ position: 'absolute', bottom: '2px', right: '4px', color: '#f59e0b', fontSize: '10px' }}><i className="fas fa-star"></i></div>)}
                </button>
            );
        }
        return dayCells;
    };
    const navBtnStyle = (isActive, isDisabled) => ({
        padding: '8px 12px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase',
        color: isActive ? '#d97706' : (isDisabled ? '#d1d5db' : '#6b7280'),
        backgroundColor: 'transparent', border: 'none', cursor: isDisabled ? 'not-allowed' : 'pointer', transition: 'color 0.2s'
    });

    return (
        <div style={{ backgroundColor: '#ffffff', padding: '16px', borderRadius: '8px', position: 'relative' }}>
            <SimpleModal show={modal.show} title={modal.title} message={modal.message} isError={modal.isError} onClose={closeModal} />
            
            <div style={{ textAlign: 'center', marginBottom: '24px', borderBottom: '1px solid #f3f4f6', paddingBottom: '16px' }}>
                <h3 style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'serif', color: '#1f2937', margin: 0 }}>Reservar Mesa</h3>
                <p style={{ fontSize: '12px', color: '#d97706', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>Vintage Praia</p>
            </div>

            <div style={{ marginBottom: '24px' }}><ImportantNote /></div>

            {loading ? <p style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>A carregar...</p> : (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                        <button type="button" onClick={() => goToStep('date')} style={navBtnStyle(activeSection === 'date', false)}>1. Data</button>
                        <span style={{ color: '#e5e7eb' }}>&gt;</span>
                        <button type="button" onClick={() => goToStep('time')} disabled={!formState.date} style={navBtnStyle(activeSection === 'time', !formState.date)}>2. Hora</button>
                        <span style={{ color: '#e5e7eb' }}>&gt;</span>
                        <button type="button" onClick={() => goToStep('guests')} disabled={!formState.time} style={navBtnStyle(activeSection === 'guests', !formState.time)}>3. Pessoas</button>
                    </div>

                    {activeSection === 'date' && (
                        <div className="animate-fade-in">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '0 8px' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setCurrentDisplayDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} 
                                    style={{ padding: '8px 12px', cursor: 'pointer', border: '1px solid #9ca3af', borderRadius: '50%', backgroundColor: 'white', color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                
                                <span style={{ fontSize: '18px', fontWeight: 'bold', textTransform: 'capitalize', fontFamily: 'serif' }}>
                                    {currentDisplayDate.toLocaleString('pt-PT', { month: 'long', year: 'numeric' })}
                                </span>
                                
                                <button 
                                    type="button" 
                                    onClick={() => setCurrentDisplayDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} 
                                    style={{ padding: '8px 12px', cursor: 'pointer', border: '1px solid #9ca3af', borderRadius: '50%', backgroundColor: 'white', color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', fontSize: '10px', fontWeight: 'bold', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase' }}>
                                {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map(d => <div key={d}>{d}</div>)}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', padding: '5px' }}>
                                {renderCalendar()}
                            </div>
                        </div>
                    )}
                    
                    {activeSection === 'eventDetails' && selectedEvent && (
                        <div style={{ backgroundColor: '#fffbeb', padding: '24px', borderRadius: '8px', border: '1px solid #fcd34d', textAlign: 'center' }}>
                            <h4 style={{ fontWeight: 'bold', fontSize: '20px', color: '#92400e', marginBottom: '8px' }}>{selectedEvent.nomeEvento}</h4>
                            <p style={{ fontSize: '14px', color: '#4b5563', marginBottom: '24px' }}>{selectedEvent.descricao}</p>
                            <button type="button" onClick={() => setActiveSection('time')} style={{ backgroundColor: '#d97706', color: 'white', padding: '12px 24px', borderRadius: '6px', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}>Ver Horários</button>
                        </div>
                    )}
                    
                    {activeSection === 'time' && <TimeStep bookingSettings={bookingSettings} onTimeSelect={handleTimeSelect} date={formState.date} getDiscountForTime={getBestDiscountForTime} selectedEvent={selectedEvent} dayData={dailyAvailability ? dailyAvailability[formState.date] : {}} />}
                    {activeSection === 'guests' && <GuestsStep onGuestsSelect={handleGuestsSelect} formState={formState} />}
                    {activeSection === 'details' && <DetailsStep onSubmit={handleDetailsSubmit} formState={formState} isSubmitting={isSubmitting} calculateDiscountBreakdown={calculateDiscountBreakdown} getDiscountForTime={getBestDiscountForTime} selectedEvent={selectedEvent} />}
                </>
            )}
        </div>
    );
};

export default BookingWidget;
